import Button from 'components/Button';
import Error from 'components/form/Error';
import Info from 'components/form/Info';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import ActionModal from 'components/Modal/ActionModal';
import { HeadButtons, PageBack, PageHeadContainer } from 'components/PageHead';
import ProfileNavigation from 'components/profileNavigation';
import ProfileLink from 'components/profileNavigation/Link';
import Space from 'components/Space';
import { MentorshipStatusValues } from 'enums/MentorshipStatus';
import React, { FC, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import Mentorship from 'types/Mentorship';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';
import MenteeDetailsRoutes from './Routes';

const HeadContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 1.5rem;
    margin-right: 1rem;
  }
`;

const ErrorContainer = styled.div`
  margin-top: 5rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AcceptButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
`;

const Buttons: FC = () => {
  const { id } = useParams();
  const { data: mentorship } = useSWR<Mentorship>(
    `/mentorship/mentor/mentee-details/${id}`,
  );

  const [interruptOpen, setInterruptOpen] = useState(false);

  const navigate = useNavigate();

  const handleInterrupt = () => {
    privateAPI
      .post(`/mentorship/mentor/interrupt-mentorship/${id}`)
      .then(() => {
        mutate(`/mentorship/mentor/mentee-details/${id}`);
        navigate('/mentor/mentorship/management');
      });
  };
  return (
    <>
      {/* INTERRUPT DRAWER */}
      <ActionModal
        open={interruptOpen}
        close={() => setInterruptOpen(false)}
        title="Demander l'interruption le mentorat"
        cancel={() => {
          setInterruptOpen(false);
        }}
        apply={handleInterrupt}
        applyText="Demander l'interruption"
      >
        <p>Souhaitez-vous vraiment demander l'interruption de ce mentorat ?</p>
      </ActionModal>

      {(
        [
          'WAITING_MENTOR',
          'LATE_MENTOR_FIRST_INTERVIEW',
          'LATE_MENTEE_FIRST_INTERVIEW',
        ] as MentorshipStatusValues[]
      ).includes(mentorship?.status as MentorshipStatusValues) && (
        <HeadButtons>
          <Link to="interviews/suggest">
            <Button color="primary">Proposer un premier entretien</Button>
          </Link>
        </HeadButtons>
      )}
      {(
        [
          'ONGOING',
          'LATE_MENTEE',
          'LATE_MENTOR',
          'FINISHED_DATE',
        ] as MentorshipStatusValues[]
      ).includes(mentorship?.status as MentorshipStatusValues) &&
        mentorship?.mentee?.menteeStatus === 'MENTORSHIP_ACTIVE' && (
          <HeadButtons>
            <Link to="interviews/suggest">
              <Button color="primary">Nouvel entretien</Button>
            </Link>
          </HeadButtons>
        )}
    </>
  );
};

type MenteeDetailsContextType = {
  mentee: User | undefined;
};
export const MenteeDetailsContext = React.createContext(
  {} as MenteeDetailsContextType,
);
const { Provider } = MenteeDetailsContext;

const MenteeDetails: FC = ({ children }) => {
  const { id } = useParams();
  const { data: mentorship, error } = useSWR<Mentorship>(
    `/mentorship/mentor/mentee-details/${id}`,
  );

  const value = useMemo(
    () => ({
      mentee: mentorship?.mentee,
    }),
    [mentorship],
  );
  return error ? (
    <>
      <PageHeadContainer>
        <HeadContainer>
          <PageBack href="/mentor/mentorship/management/mentees/" />
          <h1>Mentoré</h1>
        </HeadContainer>
      </PageHeadContainer>
      <ErrorContainer>
        {error?.response?.status === 400 ? (
          <Error>Vous ne pouvez pas voir les informations de ce mentoré.</Error>
        ) : (
          <Error>Une erreur est survenue</Error>
        )}
      </ErrorContainer>
    </>
  ) : mentorship?.mentee ? (
    <Provider value={value}>
      <PageHeadContainer>
        <HeadContainer>
          <PageBack href="/mentor/mentorship/management/mentees/" />
          <h1>
            {mentorship?.mentee &&
              `${mentorship.mentee.firstName} ${mentorship.mentee.lastName}`}
          </h1>
        </HeadContainer>
        <Buttons />
      </PageHeadContainer>
      {children}
    </Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={2} />
    </LoaderContainer>
  );
};

const Navigation: FC = () => {
  const { id } = useParams();
  const baseURL = `/mentor/mentorship/management/mentees/details/${id}/`;
  const { data: mentorship } = useSWR<Mentorship>(
    `/mentorship/mentor/mentee-details/${id}`,
  );

  const [refuseOpen, setRefuseOpen] = useState(false);
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [refuseLoading, setRefuseLoading] = useState(false);
  const navigate = useNavigate();

  const handleRefuse = () => {
    setRefuseLoading(true);
    privateAPI
      .post(`/mentorship/mentor/refuse-mentorship/${id}`)
      .then(() => {
        mutate(`/mentorship/mentor/mentee-details/${id}`);
        navigate('/mentor/mentorship/management');
      })
      .finally(() => {
        setRefuseLoading(false);
      });
  };

  const handleAccept = () => {
    setLoadingAccept(true);
    privateAPI
      .post(`/mentorship/mentor/accept-mentorship/${id}`)
      .then(() => {
        navigate('interviews/suggest');
        mutate(`/mentorship/mentor/mentee-details/${id}`);
      })
      .finally(() => {
        setLoadingAccept(false);
      });
  };

  return (
    <>
      {/* REFUSE MODAL */}
      <ActionModal
        open={refuseOpen}
        close={() => setRefuseOpen(false)}
        color="error"
        title="Refuser le mentorat"
        apply={handleRefuse}
        applyText="Refuser le mentorat"
        loading={refuseLoading}
      >
        <p>Souhaitez-vous vraiment refuser ce mentoré ?</p>
        <p>Cette action est irreversible.</p>
      </ActionModal>
      <ProfileNavigation>
        <ProfileLink to={baseURL}>Informations générales</ProfileLink>
        <ProfileLink to={baseURL + 'form/'}>
          Questionnaire de mentorat
        </ProfileLink>
        <ProfileLink to={baseURL + 'interviews/'}>
          Entretiens programmés
        </ProfileLink>
      </ProfileNavigation>

      {mentorship?.status === 'MENTOR_ACCEPTING' && (
        <>
          <Info>
            Vous devez choisir d'accepter ou de refuser le mentorat avec ce
            mentoré.
          </Info>
          <AcceptButtonsContainer>
            <Button
              variant="outlined"
              onClick={() => {
                setRefuseOpen(true);
              }}
            >
              Refuser
            </Button>
            <Button
              color="primary"
              onClick={handleAccept}
              loading={loadingAccept}
            >
              Accepter
            </Button>
          </AcceptButtonsContainer>
        </>
      )}
      {(
        ['FINISHED_ADMIN', 'FINISHED_DATE'] as MentorshipStatusValues[]
      ).includes(mentorship?.status as MentorshipStatusValues) && (
        <>
          <Info>
            Si vous ne l'avez pas encore fait, nous vous invitons à remplir
            votre bilan de fin de mentorat en suivant{' '}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSc6zME_XMSeMGGenQElKq7SkcIdFMkDvCHPfE4csn-nSN3uCQ/viewform?usp=sf_link"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              ce lien
            </a>
            .
          </Info>
          <Space height={1} />
        </>
      )}
    </>
  );
};

const Usage: FC = (props) => {
  return (
    <MenteeDetails {...props}>
      <Navigation />
      <MenteeDetailsRoutes />
    </MenteeDetails>
  );
};

export default Usage;
